/**
 * FL-132: デモデータ統合とリセット機能の拡張
 *
 * 作成したデモデータ（案件・スケジュール・AI配車シナリオ）を
 * 既存のデモリセット機能に統合し、リアルなデモ体験を実現
 *
 * 更新内容：
 * - 陸送員スケジュールテーブル (company_transporter_schedules) 対応
 * - AI配車デモシナリオデータ統合
 * - 充実化された案件データ統合
 */

import { isValidDate } from '../../constants/dates';
import { DEMO_TIMELINE } from './timelineData';
import { DEMO_CASES, TODAY_CASES, HISTORICAL_CASES, SCHEDULED_CASES } from './caseData';
import { DEMO_SCHEDULES, WEEKLY_SCHEDULE } from './scheduleData';
import { AI_DISPATCH_SCENARIOS } from './aiDispatchScenarios';
import { DEMO_BUSINESS_PARTNERS, PARTNER_STATS } from './businessPartnerData';
import { DEMO_DRIVER_SCHEDULES, DEMO_SCHEDULE_STATS } from './driverScheduleData';
import { loadDemoCasesToDatabase } from './demoCaseDataLoader';
import { loadDemoDriverSchedules } from './driverScheduleDataLoader';
import { logger } from '../../utils/logger';
import { DemoDataIntegration, DemoResetResult, DemoResetProgressCallback } from '../../types';

/**
 * VITE_FIXED_DATEまたはフォールバック日付を取得
 */
const getFixedDate = (): string => {
  const fixedDate = import.meta.env.VITE_FIXED_DATE;
  return fixedDate && isValidDate(fixedDate) ? fixedDate : '2025-07-22';
};

/**
 * FL-132対応: 新規テーブル追加に伴うデモリセット設定更新
 */
export const ENHANCED_DEMO_TABLES = [
  // 既存のテーブル（FL-127で実装済み）
  'business_partners',
  'business_partner_locations',
  'company_cases',
  'company_land_transporters',
  'company_ocr_settings',
  'company_partner_selections',
  'company_time_settings',

  // FL-132で新規追加
  'company_transporter_schedules', // 陸送員スケジュールテーブル

  // FL-246で新規追加（陸送員稼働スケジュール管理）
  'driver_schedules', // ドライバースケジュール管理テーブル
];

/**
 * デモデータリセット用の統合データセット
 */
export const DEMO_DATA_INTEGRATION: DemoDataIntegration = {
  // メタデータ
  metadata: {
    demoDate: getFixedDate(),
    version: '2.0.0', // FL-132対応でバージョンアップ
    createdFor: 'FL-132',
    description: 'リアルなデモ体験用データセット（案件・スケジュール・AI配車シナリオ）',
    lastUpdated: new Date().toISOString(),
  },

  // 基準日付設定
  timeline: DEMO_TIMELINE as any,

  // 案件データ（FL-132で充実化）
  cases: {
    historical: HISTORICAL_CASES,
    today: TODAY_CASES,
    scheduled: SCHEDULED_CASES,
    total: DEMO_CASES as any,
    stats: {
      totalCases: HISTORICAL_CASES.length + TODAY_CASES.length + SCHEDULED_CASES.length,
      todayCases: TODAY_CASES.length,
      unassignedToday: TODAY_CASES.filter(c => !c.assignedLandTransporterId).length,
    } as any,
  },

  // 陸送員スケジュール（FL-132で新規実装）
  schedules: {
    transporters: DEMO_SCHEDULES.TRANSPORTERS,
    weekly: WEEKLY_SCHEDULE as any,
    fridayDetail: WEEKLY_SCHEDULE['2025-07-10'] as any,
    stats: DEMO_SCHEDULES.STATS as any,
  },

  // AI配車シナリオ
  aiDispatch: {
    scenarios: AI_DISPATCH_SCENARIOS,
    optimization: {
      before: AI_DISPATCH_SCENARIOS.BEFORE,
      after: AI_DISPATCH_SCENARIOS.AFTER,
      improvements: AI_DISPATCH_SCENARIOS.AFTER.improvements,
    },
  },

  // 取引先データ（FL-132追加）
  businessPartners: {
    partners: DEMO_BUSINESS_PARTNERS,
    stats: PARTNER_STATS as any,
    count: DEMO_BUSINESS_PARTNERS.length,
  },

  // ドライバースケジュール（FL-246で新規実装）
  driverSchedules: {
    schedules: DEMO_DRIVER_SCHEDULES,
    stats: DEMO_SCHEDULE_STATS as any,
    count: DEMO_DRIVER_SCHEDULES.length,
  },
} as const;

/**
 * デモリセット時の日付固定処理
 */
export const DEMO_DATE_RESET_FUNCTIONS = {
  /**
   * システム日付をVITE_FIXED_DATEに固定（フロントエンド用）
   */
  setDemoDateMode(): void {
    // ローカルストレージに固定日付を設定
    if (typeof window !== 'undefined') {
      const fixedDate = getFixedDate();
      localStorage.setItem('DEMO_FIXED_DATE', fixedDate);
      localStorage.setItem('DEMO_MODE_ACTIVE', 'true');

      logger.info('Demo date fixed', {
        component: 'DemoDateReset',
        demoDate: fixedDate,
      });
    }
  },

  /**
   * 固定日付モードを解除
   */
  clearDemoDateMode(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('DEMO_FIXED_DATE');
      localStorage.removeItem('DEMO_MODE_ACTIVE');

      logger.info('Demo date mode cleared', {
        component: 'DemoDateReset',
      });
    }
  },

  /**
   * 現在の固定日付を取得
   */
  getFixedDate(): string | null {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('DEMO_FIXED_DATE');
    }
    return null;
  },

  /**
   * デモモードかどうかを確認
   */
  isDemoModeActive(): boolean {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('DEMO_MODE_ACTIVE') === 'true';
    }
    return false;
  },

  /**
   * デモ用の現在日時を取得
   */
  getDemoCurrentDate(): Date {
    const fixedDate = this.getFixedDate();
    if (fixedDate && this.isDemoModeActive()) {
      // 固定日付の時刻部分を現在時刻に設定
      const now = new Date();
      const demo = new Date(fixedDate);
      demo.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
      return demo;
    }
    return new Date();
  },
};

/**
 * デモデータリセット統合関数
 */
export const DEMO_RESET_INTEGRATION = {
  /**
   * フルデモリセット（日付固定 + データリセット）
   */
  async executeFullDemoReset(
    resetService: any,
    onProgress?: DemoResetProgressCallback
  ): Promise<
    DemoResetResult & {
      details: {
        dateFixed: boolean;
        dataReset: boolean;
        caseCount: number;
        transporterCount: number;
        partnerCount: number;
        aiScenariosLoaded: boolean;
      };
    }
  > {
    const totalSteps = 5;
    let currentStep = 1;

    try {
      // Step 1: 日付を2025年7月11日に固定
      onProgress?.('デモ日付を2025年7月11日に固定しています...', currentStep++, totalSteps);
      DEMO_DATE_RESET_FUNCTIONS.setDemoDateMode();

      // Step 2: 既存のデモリセット実行
      onProgress?.('デモデータをリセットしています...', currentStep++, totalSteps);
      const resetResult = await resetService.safeResetDemoData({
        onProgress: (msg: string) => onProgress?.(msg, currentStep, totalSteps),
        createBackup: false,
      });

      if (!resetResult.success) {
        throw new Error(`データリセットに失敗しました: ${resetResult.message}`);
      }

      // Step 3: 新しいデモデータの準備と投入
      onProgress?.('FL-132/FL-246デモデータを準備しています...', currentStep++, totalSteps);

      // 案件データをデータベースに投入
      const caseLoadResult = await loadDemoCasesToDatabase();
      if (!caseLoadResult.success) {
        logger.warn('Failed to load demo cases', {
          component: 'DemoResetIntegration',
          message: caseLoadResult.message,
        });
      }

      // ドライバースケジュールデータをデータベースに投入
      const scheduleLoadResult = await loadDemoDriverSchedules();
      if (!scheduleLoadResult.success) {
        logger.warn('Failed to load demo driver schedules', {
          component: 'DemoResetIntegration',
          message: scheduleLoadResult.message,
        });
      }

      // データの検証
      const caseCount = caseLoadResult.success
        ? caseLoadResult.counts.total
        : DEMO_DATA_INTEGRATION.cases.stats.totalCases;
      const transporterCount = DEMO_DATA_INTEGRATION.schedules.stats.TOTAL_TRANSPORTERS;
      const partnerCount = DEMO_DATA_INTEGRATION.businessPartners.count;
      const driverScheduleCount = scheduleLoadResult.success
        ? scheduleLoadResult.loadedCount || 0
        : DEMO_DATA_INTEGRATION.driverSchedules.count;
      const aiScenariosLoaded = Object.keys(AI_DISPATCH_SCENARIOS).length > 0;

      // Step 4: ブラウザリフレッシュの案内
      onProgress?.('デモ環境の準備が完了しました', currentStep++, totalSteps);

      return {
        success: true,
        message: `デモリセットが完了しました。日付は${getFixedDate()}に固定されています。`,
        loadedData: {
          caseCount,
          transporterCount,
          partnerCount,
          aiScenariosLoaded,
        },
        details: {
          dateFixed: DEMO_DATE_RESET_FUNCTIONS.isDemoModeActive(),
          dataReset: resetResult.success,
          caseCount,
          transporterCount,
          partnerCount,
          driverScheduleCount,
          aiScenariosLoaded,
        },
      };
    } catch (error) {
      logger.error('Full reset failed', {
        component: 'DemoResetIntegration',
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      return {
        success: false,
        message: `デモリセットに失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
        loadedData: null,
        details: {
          dateFixed: false,
          dataReset: false,
          caseCount: 0,
          transporterCount: 0,
          partnerCount: 0,
          aiScenariosLoaded: false,
        },
      };
    }
  },

  /**
   * デモデータの整合性チェック
   */
  validateDemoData(): {
    isValid: boolean;
    issues: string[];
    summary: {
      totalCases: number;
      todayunassigned: number;
      activeTransporters: number;
      aiOptimizationReady: boolean;
    };
  } {
    const issues: string[] = [];

    // 案件データの整合性チェック
    if (TODAY_CASES.length === 0) {
      issues.push('当日案件データが存在しません');
    }

    const unassignedToday = TODAY_CASES.filter(c => !c.assignedLandTransporterId).length;
    if (unassignedToday === 0) {
      issues.push('未配車案件がないため、AI配車デモができません');
    }

    // 陸送員データの整合性チェック
    const tuesdaySchedule = (WEEKLY_SCHEDULE as any)['2025-07-22'];
    const activeTransporterCount = tuesdaySchedule?.activeTransporters?.length || 0;
    if (!tuesdaySchedule || activeTransporterCount === 0) {
      issues.push('7月22日の陸送員スケジュールが設定されていません');
    }

    // AI配車データの整合性チェック
    const beforeOptimization = (AI_DISPATCH_SCENARIOS as any).BEFORE;
    const unassignedCount = beforeOptimization?.unassignedCases?.length || 0;
    if (unassignedCount === 0) {
      issues.push('AI配車最適化用の未配車案件データがありません');
    }

    // 統計サマリー
    const summary = {
      totalCases: DEMO_DATA_INTEGRATION.cases.stats.totalCases,
      todayunassigned: unassignedToday,
      activeTransporters: tuesdaySchedule?.activeTransporters?.length || 0,
      aiOptimizationReady:
        unassignedToday >= 3 && (tuesdaySchedule?.activeTransporters?.length || 0) >= 2,
    };

    return {
      isValid: issues.length === 0,
      issues,
      summary,
    };
  },

  /**
   * デモ体験の推奨フロー取得
   */
  getRecommendedDemoFlow(): {
    phase: string;
    title: string;
    description: string;
    estimatedTime: string;
    actions: string[];
  }[] {
    return [
      {
        phase: '1',
        title: 'ダッシュボード確認',
        description: '2025年7月11日（金曜日）の業務状況を確認',
        estimatedTime: '2分',
        actions: [
          '当日案件数（8件）の確認',
          '未配車案件（5件）の確認',
          '陸送員稼働状況（6名アクティブ、2名休暇）',
          '週間案件分布の確認',
        ],
      },
      {
        phase: '2',
        title: '手動配車体験',
        description: '従来の手動配車プロセスを体験',
        estimatedTime: '3分',
        actions: [
          '未配車案件「case-today-003」を選択',
          '利用可能陸送員「鈴木 次郎」に配車',
          '配車時の制約確認（勤務時間、スキル）',
          '手動配車の課題認識',
        ],
      },
      {
        phase: '3',
        title: 'AI配車最適化',
        description: 'AI配車エンジンによる自動最適化',
        estimatedTime: '3分',
        actions: [
          'AI配車ボタンをクリック',
          '最適化計算の実行（3秒）',
          '最適化結果の確認',
          '改善効果の数値確認（距離31km削減、時間1.9時間短縮）',
        ],
      },
      {
        phase: '4',
        title: '効果確認・レポート',
        description: '最適化効果の詳細確認',
        estimatedTime: '2分',
        actions: [
          'ルートマップでの視覚的確認',
          'コスト削減効果（約15%）',
          'レポート機能での実績確認',
          'CSV出力機能の確認',
        ],
      },
    ];
  },
};

/**
 * デモデータ統合ユーティリティ関数
 */
export const demoDataUtils = {
  /**
   * 特定の日付の案件データを取得
   */
  getCasesForDate(date: string) {
    const fixedDate = getFixedDate();
    if (date === fixedDate) {
      return TODAY_CASES;
    }
    if (date < fixedDate) {
      return HISTORICAL_CASES.filter(c => c.pickupDate === date);
    }
    return SCHEDULED_CASES.filter(c => c.pickupDate === date);
  },

  /**
   * 特定の日付の陸送員スケジュールを取得
   */
  getScheduleForDate(date: string) {
    return (WEEKLY_SCHEDULE as any)[date] || null;
  },

  /**
   * 指定された案件IDに対するAI最適化提案を取得
   */
  getAIOptimizationForCase(caseId: string) {
    // 最適化前の案件を検索
    const beforeCase = AI_DISPATCH_SCENARIOS.BEFORE.unassignedCases.find(c => c.caseId === caseId);

    if (!beforeCase) return null;

    // 最適化後の配車プランを検索
    for (const assignment of AI_DISPATCH_SCENARIOS.AFTER.optimizedAssignments) {
      const optimizedCase = assignment.assignedCases.find(c => c.caseId === caseId);
      if (optimizedCase) {
        return {
          before: beforeCase,
          after: optimizedCase,
          transporter: {
            id: assignment.transporterId,
            name: assignment.transporterName,
          },
          improvements: AI_DISPATCH_SCENARIOS.AFTER.improvements,
        };
      }
    }

    return null;
  },

  /**
   * デモ体験の進捗状況を計算
   */
  calculateDemoProgress(completedPhases: string[]): {
    completedPhases: number;
    totalPhases: number;
    progressPercentage: number;
    nextPhase?: string;
  } {
    const totalPhases = DEMO_RESET_INTEGRATION.getRecommendedDemoFlow().length;
    const completed = completedPhases.length;
    const progressPercentage = Math.round((completed / totalPhases) * 100);

    const nextPhaseIndex = completed;
    const nextPhase =
      nextPhaseIndex < totalPhases
        ? DEMO_RESET_INTEGRATION.getRecommendedDemoFlow()[nextPhaseIndex].title
        : undefined;

    return {
      completedPhases: completed,
      totalPhases,
      progressPercentage,
      nextPhase,
    };
  },
};

// デフォルトエクスポート
export default DEMO_DATA_INTEGRATION;
